/**
 * The single place that decides how a status reads on screen: which Badge variant it
 * gets, and which icon represents it. Every other file that currently paints a status
 * locally is meant to call in here instead — see
 * `docs/plan-homogeneizar-color-y-estado-en-pantalla.md`, Frente C, for the inventory
 * that justifies this file: 51 files defining 81 local functions or constants for
 * this, none with a test, 12 of them verified to disagree with each other on the same
 * status (`completed` blue in one file and green in six others; `cancelled` red and
 * gray inside the same file, `TenantAdminOrders.tsx`).
 *
 * This module never returns a class string or JSX, only the name of a Badge variant
 * (`src/components/ui/badge.tsx`) and a Lucide icon component. That is deliberate: it
 * can never drift into a hardcoded hex or a hand-paired per-theme override class,
 * because it never touches a color value at all — the Badge variant already carries
 * its own light and dark token. Enforced by the test next to this file.
 *
 * C1 shipped the module with nobody calling it yet. C2 through C8 migrated the 52 files
 * of the plan's appendix onto it, one entity group at a time: health, templates and
 * flows, orders and payments, workflows and channels, testing and alerts, messaging and
 * catalog. Each status below carries the count that decided its variant.
 */
import type { LucideIcon } from 'lucide-react';
import { Activity, AlertTriangle, CheckCircle, Clock, HelpCircle, Loader2, Package, ShoppingCart, Target, TrendingUp, Truck, XCircle } from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';
import type { badgeVariants } from '@/components/ui/badge';

/** Every Badge variant name this module is allowed to return. */
export type StatusBadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

/**
 * An entity groups the statuses that share a vocabulary — an order's `draft` is not a
 * workflow's `draft`. Left as a plain string, not a closed union: new entities arrive
 * as C2 onward migrates their files, and this module has to grow without a signature
 * change.
 */
export type StatusEntity = string;

/**
 * Statuses whose winning appearance is already verified against the codebase (plan
 * appendix, "12 colisiones verificadas valor por valor"): for each, the value below is
 * the one the most sites already use (OK25 — count first, the imperante wins). Applies
 * regardless of entity, with the one exception carved out in
 * `ENTITY_STATUS_OVERRIDES` below.
 *
 * Deliberately not exhaustive: the plan measured these 12 collisions, not the other
 * ~28 statuses that coexist in more than one file without a verified winner yet. Those
 * get added here only once a migration group (C2 onward) measures them the same way.
 */
const GENERIC_STATUS_VARIANTS: Partial<Record<string, StatusBadgeVariant>> = {
  pending: 'warning', // 7 of 15 sites; sites with a colorless `secondary` fold into this
  completed: 'success', // 6 of 7 sites
  active: 'success', // the two colorless `default` sites fold into this
  connected: 'success',
  disconnected: 'neutral',
  error: 'destructive',
  healthy: 'success',
  approved: 'success', // 3 of 5 sites; StagingReviewTable's blue `approved` names a different entity and is resolved in its own migration group
  rejected: 'destructive',
  failed: 'destructive',
  cancelled: 'neutral', // 3 of 5 sites; STATUS_COLORS.order's red `cancelled` folds into this

  // C2 (health group, 11 sites) additions below. Measured the same way: read every
  // site in the group's own inventory (plan appendix, "C2 · Salud"), count.
  warning: 'warning', // unanimous, 6 of 6 sites that have it: SystemHealthMonitor, WhatsAppHealthMonitor, WhatsAppWebhookHealth, PlatformAdmin, WhatsAppAdminPlatform, WhatsAppAdminRegular
  critical: 'destructive', // SystemHealthMonitor, its only site, already destructive there
  info: 'neutral', // a severity level, not a health status; SystemHealthMonitor's getSeverityBadge, its only site, already outline (folds into neutral)
  setup_required: 'outline', // ChannelManagementGrid, its only site, already outline there
  partial: 'warning', // WhatsAppTenantOverview's integrationStatus, its only site
  unhealthy: 'destructive', // unanimous, 2 of 2: PlatformAdminAPIHealth, TenantAdminAPIMonitoring
  checking: 'warning', // PlatformAdminAPIHealth and TenantAdminAPIMonitoring use `secondary` today (2 of 2); promoted to the group's own `warning` for "not settled yet", the same meaning `checking` carries in WhatsAppAdminRegular's `checking...` (normalized to this key before lookup)
  disabled: 'neutral', // unanimous, 2 of 2: PlatformAdminAPIHealth, TenantAdminAPIMonitoring (both `outline` today, folds into neutral)
  unknown: 'neutral', // the catch-all case across the group; every site that has it renders it gray/outline today
  auth_failed: 'destructive', // WhatsAppWebhookHealth's `auth_failed` and WhatsAppAdminRegular's `auth failed` (space, normalized to this key before lookup) — same meaning, both already red
  // Added by C4 (orders, payments, charges, fulfillment, documents, carts and staging --
  // `docs/plan-homogeneizar-color-y-estado-en-pantalla.md`), measured the same way as the 12
  // collisions above: every site that paints each status, read and counted.
  confirmed: 'info', // order, payment and fulfillment agree: TenantAdminOrders.tsx and CustomerEditSheet.tsx paint it blue; `document`'s own `confirmed` means something else -- see the entity override below
  processing: 'info', // STATUS_COLORS.order and CHARGE_STATUS_COLORS both used indigo/blue; unreachable order status, see TenantAdminOrders.tsx header, but the color still has to exist for the day it is reachable + C8: WhatsAppMessageQueue.tsx and ProductCatalogHistory.tsx agree, no counter-example found
  on_hold: 'warning', // STATUS_COLORS.order and STATUS_COLORS.fulfillment in TenantAdminOrders.tsx both used an orange/amber family
  authorized: 'info', // STATUS_COLORS.payment, TenantAdminOrders.tsx, the only site
  paid: 'success', // STATUS_COLORS.payment and CHARGE_STATUS_COLORS both green
  partially_paid: 'warning', // STATUS_COLORS.payment, amber
  partially_refunded: 'warning', // CHARGE_STATUS_COLORS, amber
  unfulfilled: 'neutral', // STATUS_COLORS.fulfillment, plain gray
  shipped: 'info', // STATUS_COLORS.fulfillment, cyan -- folds into the blue family, no separate token for it
  in_transit: 'info', // STATUS_COLORS.fulfillment, sky -- same fold as `shipped`
  delivered: 'success', // STATUS_COLORS.fulfillment, green
  delayed: 'warning', // STATUS_COLORS.fulfillment, orange
  extracted: 'neutral', // TenantAdminDocumentReview.tsx STATUS_COLORS, slate -- a document's own vocabulary, no collision with any other entity
  needs_confirmation: 'warning', // TenantAdminDocumentReview.tsx STATUS_COLORS, amber
  auto_approved: 'success', // StagingReviewTable.tsx getStatusBadge, green
  abandoned: 'warning', // TenantAdminOrders.tsx CartStatusBadge, amber
  created: 'info', // StatusTimeline.tsx EVENT_ICONS, the only site that paints this timeline-event tag; blue, matching `confirmed`'s family
  expired: 'neutral', // 2 of 3 sites (CustomerEditSheet.tsx and TenantAdminOrders.tsx both stone/gray); PaymentMessage.tsx's orange is the outlier and loses
  suspended: 'destructive', // 2 of 2 sites (C6): PlatformAdminBilling.tsx, PlatformAdminTenants.tsx
  testing: 'outline', // PlatformAIModelManager (C7): a connection check in progress
  never_tested: 'secondary', // PlatformAIModelManager (C7): no check has run yet
  accepted: 'success', // C8: AgentHandoffsPanel.tsx, the only site seen so far
};

/**
 * Per-entity overrides. Two reasons a status lands here instead of in the generic map
 * above: the word means something different depending on the entity (`draft`: a
 * workflow's `draft` means still being built — warning — while an order's or a
 * template's `draft` means simply not finalized yet — neutral — same word, two
 * different meanings), or the status simply has not been measured for any other entity
 * yet, so scoping it here is the only claim this module can honestly make (every
 * `template` status added below, and the whole `whatsapp_flow` entity, are the second
 * kind — see the migration group that added them for its evidence).
 */
const ENTITY_STATUS_OVERRIDES: Partial<Record<StatusEntity, Partial<Record<string, StatusBadgeVariant>>>> = {
  // `archived` verified for `workflow` only (C5): WorkflowCard.tsx and WorkflowViewDialog.tsx,
  // gray in both, agreeing. Not promoted to the generic map because no other entity's
  // `archived` has been measured yet.
  workflow: { draft: 'warning', archived: 'neutral' },
  order: { draft: 'neutral' },
  template: {
    draft: 'neutral',
    // C3 (WhatsApp templates and flows): measured against
    // MetaTemplateCard.tsx's STATUS_COLORS, the winning superset for this group (11
    // statuses; TemplatesFilterBar.tsx's own comment declares it copies that file's
    // colors). `approved`/`pending`/`rejected` already match the generic map above and
    // need no override here.
    published: 'success',
    disabled: 'neutral',
    in_appeal: 'info',
    deleted: 'neutral',
    deprecated: 'neutral',
    flagged: 'warning',
    // `paused` is deliberately absent: every site that colors it paints it orange, and
    // none of the eight Badge variants carries that hue. Forcing it into `warning` would
    // lose information that exists today, so the sites that color it keep their own
    // local, uncalled-for-here class instead of calling this module for it.
  },
  // C3: a Meta Flow's status is a different vocabulary from a template's — uppercase,
  // straight off Meta's own form (WhatsAppFlowSelector.tsx, TenantWhatsAppFlows.tsx):
  // `PUBLISHED` / `DRAFT` / `DEPRECATED`. Kept as its own entity so it never folds into
  // `template`'s lowercase `draft`.
  whatsapp_flow: {
    PUBLISHED: 'success',
    DRAFT: 'neutral',
    DEPRECATED: 'neutral',
  },
  // A document's `confirmed` means the extracted fields were reviewed and accepted (success),
  // not "the order moved to its confirmed stage" (info, the generic meaning above). Same word,
  // unrelated lifecycles -- TenantAdminDocumentReview.tsx, C4.
  document: { confirmed: 'success' },
  // Staging's `approved` names extraction confidence, not a template's approval -- the generic
  // `approved: 'success'` above is for templates. StagingReviewTable.tsx already painted this
  // one blue, distinct from its own green `auto_approved`, so it keeps its own value here
  // instead of folding into the generic green. C4.
  staging: { approved: 'info' },
  // AIAPIControlPanel.tsx (C6) derives its status from three booleans, not a string;
  // its adapter names these three before calling this module. `active` is left out on
  // purpose: that file never painted it with a real color, only the Badge `default`.
  ai_api_config: { disabled: 'destructive', emergency_stop: 'destructive', circuit_breaker: 'secondary' },
  // C7 (PlatformTestingDashboard, TenantTestingSuite): `completed` is not a single
  // appearance for this entity -- it subdivides by a numeric success-rate threshold that
  // stays local to those two files (a calculation, not a status). The three outcomes get
  // their own synthetic status names here so the threshold's *result* still comes from
  // this module instead of being hand-painted at each call site.
  test_run: {
    running: 'secondary',
    not_verified: 'neutral',
    completed_high: 'success',
    completed_medium: 'warning',
    completed_low: 'destructive',
  },
  // C7 (AISuggestionDebugger): `pending` and `error` already match the generic map;
  // `success` is the one value this entity needs of its own.
  debug_step: { success: 'success' },
  // C7 (ModulePermissionMatrix): a channel-compatibility grade, not an object's
  // lifecycle status -- migrated anyway because the plan lists it, with that caveat kept
  // here so nobody reads `full`/`partial`/`limited` as a status vocabulary to reuse.
  channel_compatibility: { full: 'success', partial: 'warning', limited: 'destructive' },
  // C7 (TenantUsageDashboard, UsageAlertsPanel): a severity rating, not a lifecycle
  // status either -- same caveat as above. `low`/`medium`/`high` come from
  // TenantUsageDashboard, `warning`/`critical`/`resolved` from UsageAlertsPanel; both
  // files share this one vocabulary so the two homogenize to the same appearance.
  usage_alert_severity: {
    low: 'default',
    medium: 'warning',
    high: 'destructive',
    warning: 'warning',
    critical: 'destructive',
    resolved: 'success',
  },
  // C7 (WebhookErrorAlert): the source is a boolean (`authenticationFailing`), translated
  // to a status name before reaching this module. `error` already matches the generic
  // map; `degraded` (a lesser, non-auth issue) does not, and is deliberately kept to a
  // variant `Alert` itself supports (`default`/`destructive` only -- see alert.tsx).
  webhook_health: { degraded: 'default' },
  // C8: WhatsAppMessageQueue.tsx's `sent` reads success (the message left OK), while
  // TenantConversationMessages.tsx's `message_delivery.sent` reads neutral (it is
  // the least-informative of five delivery states, not a success worth highlighting)
  // — same word, two different meanings, so it cannot be generic.
  message_queue: { sent: 'success' },
  message_delivery: { read: 'info', delivered: 'success', sent: 'neutral', sending: 'warning' },
  // C8: `open`/`expiring_soon`/`template_only` are the only three values
  // `conversations_status_check` allows (see ConversationListItem.tsx).
  conversation_window: { open: 'success', expiring_soon: 'warning', template_only: 'warning' },
  // C8: TenantConversationDetails.tsx's `getSummaryStatusColor` — `pending` here means
  // the AI summary has not been generated yet, which reads as a quiet `secondary`, not
  // the `warning` a queued message earns elsewhere.
  conversation_summary: { pending: 'secondary', generated: 'success' },
  // C8: ProductCatalogHistory.tsx — `pending` here is a catalog upload that has not
  // started, which the file already read as `secondary`, not the `warning` most other
  // `pending` sites use.
  catalog_upload: { pending: 'secondary' },
  // C8: TenantContextStores.tsx — not an object's state, a rating computed from a
  // performance metric. Kept per-entity because the three labels are unique to this
  // screen, not a shared vocabulary.
  context_store_performance: {
    'High Performance': 'success',
    'Average Performance': 'warning',
    'Poor Performance': 'destructive',
  },
  // Found migrating the inventory that verifies this frontier (scripts/status-appearance-inventory.mjs):
  // APIHealthHistoryChart.tsx's `getUptimeStatus` bucketed a rolling success rate into
  // 'default'/'secondary'/'destructive' on its own. Kept the same three buckets (99% / 95% /
  // 90% thresholds stay local, they are a calculation, not a status — same reasoning as
  // `test_run` above) but now names the bucket and asks this module, so the variant itself
  // lives in one place instead of two.
  api_uptime: { excellent: 'success', good: 'success', fair: 'warning', poor: 'destructive' },
  // StagingReviewTable.tsx's `getConfidenceBadge` bucketed an extraction-confidence score
  // (>=0.7 / >=0.4 / below) into raw `bg-green-100`/`bg-yellow-100`/`bg-red-100`, with no
  // theme-variant pair at all -- the threshold stays local (a calculation), the resulting
  // color now comes from here.
  staging_confidence: { high: 'success', medium: 'warning', low: 'destructive' },
  // LogDetailsModal.tsx and LogsTable.tsx each carried an identical `getLevelBadgeVariant`
  // switch over a log's severity level. Centralized with the same four variants both files
  // already agreed on.
  log_level: { ERROR: 'destructive', WARN: 'secondary', INFO: 'default', DEBUG: 'outline' },
  // TenantConversationDetails.tsx's `getStatusColor` compared a live count against a trigger
  // threshold (>=trigger / >=80% of trigger / below) to pick 'destructive'/'secondary'/'outline'
  // on its own. The ratio calculation stays local; the resulting variant now comes from here.
  conversation_engagement: { at_limit: 'destructive', near_limit: 'secondary', normal: 'outline' },
  // WhatsAppWebhookDebugger.tsx's `getStatusColor`/`getStatusIcon` read a single boolean
  // (a test call either worked or did not) and returned 'default'/'destructive' plus
  // CheckCircle/XCircle on their own. Same two outcomes, now named and resolved here.
  webhook_test_probe: { ok: 'default', failed: 'destructive' },
  // APIHealthHistoryChart.tsx's chart-tooltip `CustomTooltip` read a single boolean
  // (`data.success`) and returned raw `text-green-500`/`text-red-500` on its own, with
  // no prior Badge variant tying it to a particular vocabulary -- free to land on the
  // semantic success/destructive tokens the rest of this migration uses.
  api_health_probe: { ok: 'success', failed: 'destructive' },
  // OutboundTestPanel.tsx's `getStatusColor` bucketed an HTTP status code into
  // 'default'/'secondary'/'destructive' on its own (no response and 4xx/5xx both read
  // destructive there today — kept exactly as it was, only centralized).
  outbound_test_response: { success_2xx: 'default', redirect_3xx: 'secondary', client_error_4xx: 'destructive', server_error_5xx: 'destructive', no_response: 'destructive' },
  // WhatsAppErrorPanel.tsx's `getErrorVariant` switched on a message-send error's own type
  // vocabulary (distinct from any object's lifecycle status) into the two variants the
  // `Alert` component supports. Same two outcomes as before, now named and resolved here.
  message_error_type: { closed_window: 'default', rate_limit: 'destructive', invalid_template: 'default', api_error: 'destructive' },

  // The badges below migrate the ~60 sites the original homogenization plan left out of
  // its own appendix, calling that "another job" -- corrected: it wasn't. Same method as
  // everything above, one entity per object whose own field this is, count written next
  // to each value.
  //
  // AISuggestionPreview.tsx (1 site): a suggestion's own validation outcome, not any
  // other entity's lifecycle -- green/yellow in that file, kept as success/warning.
  ai_suggestion: { valid: 'success', needs_review: 'warning' },
  // AISuggestionPreview.tsx (2 sites: node impacts and edge impacts, same file): a diff's
  // own change kind, green/blue/red there -- success/info/destructive keeps the three
  // outcomes visually distinct.
  workflow_diff: { added: 'success', modified: 'info', removed: 'destructive' },
  // RoutePreviewCard.tsx (1 site): whether a downstream node was matched to the route;
  // `unmatched` already used the real `destructive` variant, only `matched`'s green-600
  // was hand-painted.
  route_preview: { matched: 'success' },
  // A flow/conversation rehearsal's own three-way verdict, unanimous across the two
  // testing screens that render it (FlowSweepPanel.tsx, PlatformAdminConversationTesting.tsx):
  // CLEAN green, DIRTY already `destructive`, NOT VERIFIED already a deliberately distinct
  // gray outline (see PlatformAdminConversationTesting.tsx's own comment on that branch) --
  // only CLEAN was hand-painted, so only CLEAN is registered here.
  flow_sweep_verdict: { CLEAN: 'success' },
  // AnalyticsDashboard.tsx (3 sites, one per metric row): a canned quality-rating label
  // per row, not a shared vocabulary with any other entity. `good`'s purple had no real
  // meaning to lose distinct from `excellent` -- each row's own text already carries the
  // distinction, the badge is a positive/neutral accent only -- so both read `success`.
  ai_metric_rating: { excellent: 'success', high: 'info', good: 'success' },
  // CatalogBrowserDialog.tsx's `catalog_status` (1 site, 4 values): `update_available`
  // (indigo) is deliberately absent -- folding it into `info` would make it read
  // identically to `new` (sky, also folded into `info`) in the same list, erasing the
  // difference between "never seen before" and "a newer version exists for a model
  // already added", so that one site keeps its own indigo class instead.
  catalog_model_status: { new: 'info', exists: 'success', price_changed: 'warning' },
  // PlatformAdminAIManagement.tsx (1 site): a model's access tier and its own retirement
  // status, two different fields of the same row, sharing one entity since neither
  // vocabulary collides with the other. `enterprise` (purple) is deliberately absent --
  // no variant carries that hue and it sits next to `pro` in the same column, so forcing
  // it would blur a distinction the operator relies on; that value keeps its own class.
  ai_model: { pro: 'info', retiring: 'warning', retired: 'neutral' },
  // Settings.tsx (1 site): the account's own two-factor-auth state.
  two_factor_auth: { enabled: 'success' },
  // AvailabilityChecker.tsx (1 site): a calendar slot's own availability.
  calendar_slot: { available: 'success' },
  // TenantAdminRandomERPTesting.tsx (2 sites): whether any warehouse reports stock for a
  // product in this ERP test screen.
  erp_stock: { in_stock: 'success' },
  // WhatsAppAccountDialog.tsx (1 site): the account-validation run's own pass/fail outcome.
  whatsapp_account_validation: { valid: 'success', invalid: 'destructive' },
  // WhatsAppAccountDialog.tsx (1 site, 3 of its 4 outcomes): a phone-registration attempt's
  // own result. `needs_existing_pin` (orange) is deliberately absent -- forcing it into
  // `warning` would make it read identically to `rate_limited`, the other outcome shown in
  // the same list, erasing the difference between "retry later" and "needs a PIN from the
  // operator"; that one outcome keeps its own orange class.
  whatsapp_phone_registration: { already_registered: 'info', registered: 'success', rate_limited: 'warning' },
};

/**
 * Resolves the Badge variant for a (entity, status) pair: an entity-specific override
 * first, the generic map otherwise. Returns `undefined` when neither has an answer —
 * every current caller already has its own fallback (usually `secondary` or
 * `outline`), and this module would rather stay silent than guess a value nobody
 * measured yet.
 */
export function getStatusBadgeVariant(entity: StatusEntity, status: string): StatusBadgeVariant | undefined {
  return ENTITY_STATUS_OVERRIDES[entity]?.[status] ?? GENERIC_STATUS_VARIANTS[status];
}

/**
 * Icon per status, fundamented the same way as the variant map: read every site that
 * already renders an icon for the status, and take the one the most sites agree on.
 *
 * Left out on purpose where sites disagree with no clear winner (`paid`: CreditCard in
 * `StatusTimeline.tsx`, CheckCircle2 in `PaymentMessage.tsx`, one site each) or where no
 * site paints an icon for the status yet (`approved`, `active`, `draft`). Same rule as
 * the variant map: it grows only once a migration group measures more sites, never by
 * guessing.
 */
export const STATUS_ICONS: Partial<Record<string, LucideIcon>> = {
  pending: Clock, // AgentHandoffsPanel, AISuggestionDebugger, WhatsAppMessageQueue, MessageBubble, StatusTimeline
  completed: CheckCircle, // AgentHandoffsPanel, StatusTimeline
  connected: CheckCircle, // ChannelManagementGrid, the imperante for this group (C6)
  disconnected: XCircle, // ChannelManagementGrid
  setup_required: AlertTriangle, // AppSidebar.tsx (C6) + ChannelManagementGrid, its only site, already AlertTriangle there
  healthy: CheckCircle, // unanimous across all 6 health-monitor files (C2)
  error: XCircle, // 5 of 6 health-monitor files (WhatsAppTenantOverview.tsx uses AlertCircle)
  failed: XCircle, // PlatformAIModelManager, PaymentMessage, WhatsAppMessageQueue
  rejected: XCircle, // AgentHandoffsPanel

  // C2 (health group) additions below, same method as the variants above.
  warning: AlertTriangle, // 5 of 6 sites that render an icon for it (WhatsAppHealthMonitor is the outlier, with AlertCircle)
  critical: XCircle, // SystemHealthMonitor, its only site, already XCircle there
  unhealthy: XCircle, // unanimous, 2 of 2: PlatformAdminAPIHealth, TenantAdminAPIMonitoring
  checking: Clock, // unanimous, 2 of 2: PlatformAdminAPIHealth, TenantAdminAPIMonitoring
  unknown: Clock, // the group's own catch-all icon; no majority (SystemHealthMonitor uses Activity, WhatsAppTenantOverview and WhatsAppWebhookHealth already use Clock, WhatsAppAdminRegular uses AlertTriangle for the literal and Clock for its own default branch) — Clock picked because it is already the plurality and reads as "not yet known", declared here rather than counted as consensus
  // No consensus between WhatsAppWebhookHealth's AlertCircle and WhatsAppAdminRegular's XCircle
  // (after normalizing its `auth failed` literal) for this one. Picked XCircle, for the same
  // reason `critical` and `unhealthy` above use it: this module's other `destructive` statuses
  // in this group all read as XCircle, and AlertCircle already lost the `warning` vote.
  auth_failed: XCircle,
  // Added by C4, re-counting `cancelled` now that StatusTimeline.tsx is in scope: XCircle in
  // StatusTimeline.tsx and PaymentMessage.tsx, AlertTriangle only in WhatsAppMessageQueue.tsx
  // (still C8, untouched) -- 2 of 3, XCircle wins.
  cancelled: XCircle,
  created: ShoppingCart, // StatusTimeline.tsx, the only site
  confirmed: CheckCircle, // StatusTimeline.tsx, the only site that paints an icon (not just a color) for this status
  shipped: Truck, // StatusTimeline.tsx, the only site
  delivered: Package, // StatusTimeline.tsx, the only site -- distinct from the WhatsApp message-delivery ticks in message-utils.ts, which this module never touches (OK17)
  // C7 additions below. `success` and `testing` are new status names with no prior
  // site to disagree with. `full`/`partial`/`limited` and the `test_run`/`degraded`
  // entries are per-entity (see ENTITY_STATUS_OVERRIDES) but this map has no entity
  // scoping of its own, so they are named distinctly enough not to collide with the
  // generic vocabulary above -- most importantly, `test_run`'s own `error`-shaped
  // outcomes are named `completed_*`, never `error`, so they never touch the verified
  // health-monitor `error` entry two lines up.
  success: CheckCircle, // AISuggestionDebugger: a debug step's own outcome, distinct from `completed`
  testing: Clock, // PlatformAIModelManager: a connection check in progress
  full: CheckCircle, // ModulePermissionMatrix: channel-compatibility grade, not an object status
  partial: AlertTriangle, // ModulePermissionMatrix
  limited: XCircle, // ModulePermissionMatrix
  running: Activity, // test_run entity: PlatformTestingDashboard, TenantTestingSuite
  not_verified: HelpCircle, // test_run entity: neither success nor failure
  completed_high: CheckCircle, // test_run entity: success rate >= 90
  completed_medium: AlertTriangle, // test_run entity: success rate 70-89
  completed_low: XCircle, // test_run entity: success rate < 70
  degraded: AlertTriangle, // webhook_health entity: WebhookErrorAlert's non-auth-failure branch
  processing: Loader2, // C8: WhatsAppMessageQueue, ProductCatalogHistory (both spin it)
  accepted: CheckCircle, // C8: AgentHandoffsPanel
  // Found migrating the inventory: TenantUsageDashboard.tsx's own `high`/`medium`/`low`
  // severity-icon switch, distinct from UsageAlertsPanel's `critical`/`warning` vocabulary
  // for the same `usage_alert_severity` entity, so no collision to resolve.
  high: AlertTriangle,
  medium: TrendingUp,
  low: Target,
};
